import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import {
  PAYIN__SH_INVOICE_INCLUDES,
  PAYIN__SORT_ATTRIBUTES
} from "../../../../../constants/routes/admin/accounting/payin";
import { EMBER_ROUTE_NAMES } from "../../../../../constants/routes";
import { SORT_QUERY_PARAM__SORT_TYPES } from "../../../../query-params/sort-query-param";
import { LSG_CHECKBOX_TERNARY__STATES } from "../../../../../components/lsg/lsg-checkbox-ternary";
import { SH_PAYMENT } from "../../../../../models/sh-payment";
import { BILLABLE_RECORD } from "../../../../../models/billable-record";

export default Service.extend({
  /**
   * Register Services
   */
  applicationStateManager: service('state-managers/application'),
  session: service(),
  shInvoiceCommandService : service('commands/sh-invoice-commands'),
  state: service('states/routes/admin/accounting/payin'),
  store: service(),
  sortQueryParam: service('query-params/sort-query-param'),

  currentUser: readOnly('session.currentUser'),

  /**
   * Batch all selected invoices into a new invoice.
   *
   * @param {Boolean} isSend      Set to true to send the invoice after batching.
   * @return {Ember.RSVP.Promise}
   */
  batchSelectedInvoices(isSend) {
    return new Promise((resolve, reject) => {
      this.set('state.isCreatingBatch', true);

      let invoicesToBatch = this.get('state.batchedInvoicesSelected').map(invoiceId => {
        return this.get('store').peekRecord('shInvoice', invoiceId);
      });

      this.get('shInvoiceCommandService').batchInvoices(invoicesToBatch).then(newBatchedInvoice => {
        if (isSend) {
          this.get('shInvoiceCommandService').sendInvoice(
            newBatchedInvoice
          ).then(() => {
            this.initStateManager();
            resolve(newBatchedInvoice);
          }).catch(e => {
            // gobble gobble
            reject(e);
          });

          return;
        }

        this.initStateManager();
        resolve(newBatchedInvoice);
      }).catch(e => {
        // gobble gobble
        reject(e);
      }).finally(() => this.set('state.isCreatingBatch', false));
    });
  },

  /**
   * Load the next invoices page.
   *
   * @return {*|Ember.RSVP.Promise}
   */
  loadNextInvoicesPage() {
    this.set('state.page', this.get('state.page') + 1);

    return this.loadShInvoices(
      this.get('state.page'),
      10,
      this.get('state.currentFilter'),
      this.get('state.paramSort'));
  },

  /**
   * Load the sh invoices
   *
   * @param {Number} page
   * @param {Number} count
   * @param {Object} filter
   * @param {String} sort
   * @return {Ember.RSVP.Promise}
   */
  loadShInvoices(page, count, filter, sort) {
    this.set('state.currentFilter', filter);

    if (page === 1)  {
      this.set('state.isLoadingInvoices', true);
    }

    return this.get('shInvoiceCommandService').loadShInvoices(
      page,
      count,
      filter,
      PAYIN__SH_INVOICE_INCLUDES,
      sort,
      true
    ).then(response => {
      if (page === 1) {
        this.set('state.shInvoices', response.shInvoices);
      } else {
        this.get('state.shInvoices').addObjects(response.shInvoices);
      }

      this.set('state.currentPagination', response.pagination);
    }).catch(() => {
      // gobble gobble
    }).finally(() => this.set('state.isLoadingInvoices', false));
  },

  /**
   * Reset our page state
   */
  resetToBasePage() {
    this.set('state.page', 1);
  },

  /**
   * Navigate to page 1 of invoices and apply filters
   */
  navigateToInvoicesPage(page) {
    const queryParams = this.get('state.queryParams');

    queryParams['page'] = page;

    this.get('applicationStateManager').setNextRoute(
      EMBER_ROUTE_NAMES.ADMIN__ACCOUNTING__PAYIN,
      {
        queryParams: queryParams
      }
    );
  },

  /**
   * Re-initialize the stateManager
   */
  initStateManager() {
    this.get('state').init();
  },

  /**
   * Toggle the specific checkbox in the batch select experience.
   *
   * @param {ShInvoice} shInvoice
   */
  toggleBatchedCheckboxSelected(shInvoice) {
    let batchedInvoicesSelected = this.get('state.batchedInvoicesSelected');

    if (batchedInvoicesSelected.includes(shInvoice.get('id'))) {
      batchedInvoicesSelected.removeObject(shInvoice.get('id'));
    } else {
      batchedInvoicesSelected.addObject(shInvoice.get('id'));
    }
  },

  /**
   * Toggle the select all state in the batched experience.
   */
  toggleBatchedSelectAll() {
    let batchedInvoicesSelected = this.get('state.batchedInvoicesSelected'),
      currentIsSelectAllClicked = this.get('state.isSelectAllInvoicesForBatchClicked');

    if (currentIsSelectAllClicked) {
      batchedInvoicesSelected.clear();
    } else {
      batchedInvoicesSelected.clear();
      this.get('state.shInvoices').forEach(shInvoice => {
        batchedInvoicesSelected.addObject(shInvoice.get('id'));
      });
    }

    this.set('state.isSelectAllInvoicesForBatchClicked', !currentIsSelectAllClicked);
  },

  /**
   * Toggle the company sort.
   */
  toggleSortCompanyDescending() {
    let currentCompanySort = this.get('sortQueryParam').getSort(PAYIN__SORT_ATTRIBUTES.COMPANY);
    if (currentCompanySort === SORT_QUERY_PARAM__SORT_TYPES.ASCENDING) {
      this.get('sortQueryParam').clearSort(PAYIN__SORT_ATTRIBUTES.COMPANY);
    } else {
      this.get('sortQueryParam').addSort(PAYIN__SORT_ATTRIBUTES.COMPANY, SORT_QUERY_PARAM__SORT_TYPES.ASCENDING);
    }

    this.set('state.paramSort', this.get('sortQueryParam').getSortQueryParam());
    this.set(
      'state.isCompanySortedDescending',
      !this.get('sortQueryParam').getSort(PAYIN__SORT_ATTRIBUTES.COMPANY));

    this.navigateToInvoicesPage(
      1,
      this.get('state.paramApproved'),
      this.get('state.paramAgreement'),
      this.get('state.paramApplicationId'),
      this.get('state.paramCompanyId'),
      this.get('state.paramInvoiceId'),
      this.get('state.paramStatus'),
      this.get('state.paramSort'),
      this.get('state.paramFinalStatus'),
      this.get('state.paramSentStatus'),
      this.get('state.paramUnpaid'),
      this.get('state.paramOverpaid'),
      this.get('state.paramExact'),
      this.get('state.paramPartial'),
      this.get('state.paramStripe'),
      this.get('state.paramInvoice'),
      this.get('state.paramRevisedStatus'));
  },

  /**
   * Update the state for our sort param
   *
   * @param {String} sortParam
   */
  updateStateFromSortParam(sortParam) {
    this.set('state.paramSort', sortParam);
    this.get('sortQueryParam').createMapFromQueryParam(sortParam);

    this.set(
      'state.isCompanySortedDescending',
      this.get('sortQueryParam').getSort(PAYIN__SORT_ATTRIBUTES.COMPANY) !== SORT_QUERY_PARAM__SORT_TYPES.ASCENDING);
  },

  /**
   * Update a ternary checkbox state for a praram
   *
   * @param {String} checkboxStatus     of type LSG_CHECKBOX_TERNARY__STATES.*
   * @param {String} paramName          see state.param*
   */
  updateTernaryStatus(checkboxStatus, paramName) {
    if (checkboxStatus === LSG_CHECKBOX_TERNARY__STATES.ON) {
      this.set(`state.${paramName}`, '1');
      return;
    } else if (checkboxStatus === LSG_CHECKBOX_TERNARY__STATES.OFF) {
      this.set(`state.${paramName}`, '0');
      return;
    }

    this.set(`state.${paramName}`, undefined);
  }
});
